/**
 * Iter 197 · Public GTM Briefing (read-only, unauthenticated).
 *
 * Route: /gtm/share/:token
 * Renders the two shipped sections (ICP & Personas, Positioning House)
 * of a shared GTM playbook. Deep-links can be sent to clients — the
 * page uses the un-authenticated `/api/v1/public/gtm/:token` endpoint.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Rocket, Users, Target, Layers, ShieldAlert, Loader2, Package as PackageIcon, Star } from "lucide-react";

export default function PublicGtmBriefing() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/public/gtm/${token}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.status === 404
        ? "This briefing link is invalid or has been revoked."
        : "Unable to load briefing."));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-8">
        <div className="max-w-md border border-border p-6 text-center" data-testid="public-gtm-error">
          <ShieldAlert className="w-8 h-8 text-destructive mx-auto mb-2" />
          <div className="font-semibold">Briefing not available</div>
          <p className="text-sm text-muted-foreground mt-1">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const brand = data.brand || {};
  const p = data.playbook || {};
  const icp = p.sections?.icp_personas?.icp || {};
  const personas = p.sections?.icp_personas?.personas || [];
  const pos = p.sections?.positioning_house || {};
  const offer = p.sections?.offer_pricing || {};

  return (
    <div className="min-h-screen bg-background" data-testid="public-gtm-page">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center gap-4">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={brand.name}
                    className="w-14 h-14 object-contain border border-border" />
          ) : (
            <div className="w-14 h-14 border border-border bg-muted/40 flex items-center justify-center text-lg font-bold">
              {(brand.name || "?").slice(0, 1)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
              <Rocket className="w-3 h-3" /> GTM Briefing
            </div>
            <h1 className="text-2xl font-bold truncate">{brand.name}</h1>
            {brand.one_liner && <p className="text-sm text-muted-foreground truncate">{brand.one_liner}</p>}
          </div>
          <div className="text-right shrink-0">
            <div className="text-sm font-semibold">{p.name}</div>
            {p.industry && (
              <div className="text-[11px] text-muted-foreground">{p.industry}</div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-10">
        {/* Positioning house */}
        <section data-testid="public-gtm-positioning">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-primary" />
            <h2 className="text-lg font-bold">Positioning House</h2>
          </div>
          {pos.category_pov && (
            <blockquote className="border-l-4 border-primary pl-4 py-2 mb-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Category POV</div>
              <div className="text-lg font-semibold">"{pos.category_pov}"</div>
            </blockquote>
          )}
          {pos.value_promise && (
            <div className="mb-6">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Value promise</div>
              <div className="text-base font-medium">{pos.value_promise}</div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(pos.pillars || []).map((pillar, i) => (
              <div key={i} className="border border-border bg-card p-4 space-y-2"
                      data-testid={`public-pillar-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 border border-primary text-primary text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <div className="font-semibold text-sm">{pillar.title}</div>
                </div>
                {pillar.sub && <p className="text-[13px] text-muted-foreground italic">{pillar.sub}</p>}
                {(pillar.proof || []).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Proof</div>
                    <ul className="text-xs space-y-0.5 list-disc list-inside">
                      {pillar.proof.map((x, j) => <li key={j}>{x}</li>)}
                    </ul>
                  </div>
                )}
                {(pillar.rtbs || []).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-blue-700 font-semibold">Reasons to believe</div>
                    <ul className="text-xs space-y-0.5 list-disc list-inside">
                      {pillar.rtbs.map((x, j) => <li key={j}>{x}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
          {(pos.competitor_slaying || []).length > 0 && (
            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Competitor slaying</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {pos.competitor_slaying.map((c, i) => (
                  <div key={i} className="border-l-2 border-rose-500 pl-3 py-1">
                    <div className="text-sm font-semibold">{c.competitor}</div>
                    <div className="text-xs text-muted-foreground">{c.why_we_win}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Offer & Pricing (client-safe) */}
        {(offer.packages || []).length > 0 && (
          <section data-testid="public-gtm-offer">
            <div className="flex items-center gap-2 mb-3">
              <PackageIcon className="w-4 h-4 text-primary" />
              <h2 className="text-lg font-bold">Offer &amp; Pricing</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {(offer.packages || []).map((pkg, i) => (
                <div key={i} className={`border ${pkg.popular ? "border-emerald-500 bg-emerald-50/40" : "border-border bg-card"} p-4 space-y-1 relative`}
                        data-testid={`public-package-${i}`}>
                  {pkg.popular && (
                    <span className="absolute -top-2 left-3 bg-emerald-500 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 flex items-center gap-1">
                      <Star className="w-3 h-3" /> Popular
                    </span>
                  )}
                  <div className="font-bold text-sm">{pkg.name}</div>
                  <div className="text-xl font-mono font-black">
                    {pkg.price || "—"} <span className="text-[10px] text-muted-foreground font-normal">/ {pkg.price_unit || "—"}</span>
                  </div>
                  {pkg.target_persona && (
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                      For · {pkg.target_persona}
                    </div>
                  )}
                  <ul className="text-xs space-y-0.5 mt-2 list-disc list-inside">
                    {(pkg.features || []).map((f, j) => <li key={j}>{f}</li>)}
                  </ul>
                </div>
              ))}
            </div>
            {(offer.promo_mechanics || []).length > 0 && (
              <div className="mt-4 border-l-2 border-amber-500 pl-3 space-y-1">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Active promo mechanics</div>
                {(offer.promo_mechanics || []).map((pr, i) => (
                  <div key={i} className="text-xs">
                    <b>{pr.name}</b> — {pr.terms} · {pr.valid_from || "—"} → {pr.valid_to || "ongoing"}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ICP + Personas */}
        <section data-testid="public-gtm-icp">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-primary" />
            <h2 className="text-lg font-bold">Ideal Customer Profile & Personas</h2>
          </div>          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-border bg-card p-4 space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">ICP</div>
              {icp.firmographic && <p className="text-sm">{icp.firmographic}</p>}
              <Group title="Trigger events" items={icp.trigger_events} />
              <Group title="Buying committee" items={icp.buying_committee} />
              <Group title="Disqualifiers" items={icp.disqualifiers} tone="destructive" />
            </div>
            <div className="space-y-3">
              {personas.map((per, i) => (
                <div key={i} className="border border-border bg-card p-4 space-y-2"
                        data-testid={`public-persona-${i}`}>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Persona {i + 1}</div>
                  <div className="font-semibold text-sm">{per.name}</div>
                  {per.jtbd && <div className="text-xs italic">"{per.jtbd}"</div>}
                  <div className="grid grid-cols-2 gap-2">
                    <Group title="Pains" items={per.pains} compact tone="destructive" />
                    <Group title="Gains" items={per.gains} compact tone="success" />
                  </div>
                  <Group title="Watering holes" items={per.watering_holes} compact />
                </div>
              ))}
            </div>
          </div>
        </section>

        <footer className="border-t border-border pt-6 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-2" data-testid="powered-by">
          <Layers className="w-3 h-3" />
          <span>
            Powered by{" "}
            <a href="https://acquilix.com" target="_blank" rel="noopener noreferrer"
                className="underline hover:text-foreground transition-colors">
              Acquilix<sup className="text-[0.6em] ml-0.5 font-normal">™</sup>
            </a>
            {" "}· Brand GTM · Read-only briefing · Last updated {new Date(p.updated_at).toLocaleDateString()}
          </span>
        </footer>
      </div>
    </div>
  );
}

function Group({ title, items, compact, tone }) {
  if (!items || items.length === 0) return null;
  const toneCls = tone === "destructive"
    ? "text-rose-700"
    : tone === "success" ? "text-emerald-700" : "text-muted-foreground";
  return (
    <div>
      <div className={`text-[10px] uppercase tracking-widest font-semibold ${toneCls}`}>{title}</div>
      <ul className={`${compact ? "text-[11px]" : "text-xs"} space-y-0.5 list-disc list-inside`}>
        {items.map((x, i) => <li key={i}>{x}</li>)}
      </ul>
    </div>
  );
}
